import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Share2 } from "lucide-react";
import { ReadableNavButton } from "../Sticker";

/**
 * Share menu for host dashboards.
 * Mount exactly one instance: placement="inline" (toolbar) OR placement="bar" (mobile footer).
 * Parents choose via useMediaQuery(SM_UP_QUERY) — do not CSS-hide a second copy.
 */
export default function DashboardShareActions({
  shareEvent,
  copyLink,
  copyHypeBlurb,
  placement = "bar",
  className = "",
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef(null);
  const inline = placement === "inline";

  useEffect(() => {
    setMenuOpen(false);
  }, [placement]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    function onPointerDown(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }

    function onKeyDown(e) {
      if (e.key === "Escape") setMenuOpen(false);
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  function runAction(action) {
    action();
    setMenuOpen(false);
  }

  const menuItems = (
    <>
      <button
        type="button"
        role="menuitem"
        onClick={() => runAction(shareEvent)}
        className="font-ui font-medium text-[14px] text-left px-3 py-2.5 min-h-[44px] rounded-sm hover:bg-cream focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-plum"
        data-testid="dash-share-btn"
      >
        Share on WhatsApp
      </button>
      <button
        type="button"
        role="menuitem"
        onClick={() => runAction(copyLink)}
        className="font-ui font-medium text-[14px] text-left px-3 py-2.5 min-h-[44px] rounded-sm hover:bg-cream focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-plum"
        data-testid="dash-copy-link-btn"
      >
        Copy link
      </button>
      <button
        type="button"
        role="menuitem"
        onClick={() => runAction(copyHypeBlurb)}
        className="font-ui font-medium text-[14px] text-left px-3 py-2.5 min-h-[44px] rounded-sm hover:bg-cream focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-plum"
        data-testid="dash-copy-hype-blurb-btn"
      >
        Copy share text
      </button>
    </>
  );

  if (inline) {
    return (
      <div
        className={`relative ${className}`.trim()}
        aria-label="Share event"
        data-testid="dash-share-bar"
        data-placement="inline"
        ref={rootRef}
      >
        <ReadableNavButton
          type="button"
          variant="secondary"
          onClick={() => setMenuOpen((open) => !open)}
          className="min-h-[44px]"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          data-testid="dash-share-menu-btn"
        >
          <Share2 className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
          Share event
          <ChevronDown
            className={`w-4 h-4 shrink-0 transition-transform duration-150 motion-reduce:transition-none ${menuOpen ? "rotate-180" : ""}`}
            strokeWidth={2.5}
            aria-hidden
          />
        </ReadableNavButton>

        {menuOpen && (
          <div
            role="menu"
            className="absolute top-full right-0 mt-2 min-w-[14rem] rounded-md2 border border-black/[0.08] bg-white p-1 shadow-sm flex flex-col z-50"
          >
            {menuItems}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`fixed inset-x-0 bottom-0 z-40 border-t border-black/[0.08] bg-canvas px-4 pt-3 pb-[max(12px,env(safe-area-inset-bottom))] ${className}`.trim()}
      aria-label="Share event"
      data-testid="dash-share-bar"
      data-placement="bar"
    >
      <div className="max-w-page mx-auto relative" ref={rootRef}>
        <ReadableNavButton
          type="button"
          variant="primary"
          onClick={() => setMenuOpen((open) => !open)}
          className="w-full"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          data-testid="dash-share-menu-btn"
        >
          <Share2 className="w-4 h-4 shrink-0" strokeWidth={2.5} aria-hidden />
          Share event
          <ChevronDown
            className={`w-4 h-4 shrink-0 transition-transform duration-150 motion-reduce:transition-none ${menuOpen ? "rotate-180" : ""}`}
            strokeWidth={2.5}
            aria-hidden
          />
        </ReadableNavButton>

        {menuOpen && (
          <div
            role="menu"
            className="absolute bottom-full left-0 right-0 mb-2 rounded-md2 border border-black/[0.08] bg-white p-1 shadow-sm flex flex-col z-50"
          >
            {menuItems}
          </div>
        )}
      </div>
    </div>
  );
}
